import React, { useState, useEffect, useRef } from "react";

export default function LoginTwoFactorModal({
  show = false,
  onClose,
  onVerify,
  email = "",
  loading = false,
}) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(120);
  const [timerActive, setTimerActive] = useState(true);
  const [shake, setShake] = useState(false);
  const inputRefs = useRef([]);

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setOtp(["", "", "", "", "", ""]);
      setTimer(120);
      setTimerActive(true);
      setShake(false);
      setTimeout(() => inputRefs.current[0]?.focus(), 200);
    }
  }, [show]);

  // Countdown timer
  useEffect(() => {
    if (!timerActive || timer <= 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setTimerActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerActive]);

  const formatTime = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const maskEmail = (em) => {
    if (!em) return "your registered email";
    const [local, domain] = em.split("@");
    if (!domain) return em;
    return local.substring(0, 2) + "****" + local.slice(-1) + "@" + domain;
  };

  const handleInput = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp];
    newOtp[idx] = val;
    setOtp(newOtp);
    if (val && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    pasted.split("").forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtp(newOtp);
    const nextEmpty = newOtp.findIndex((v) => !v);
    inputRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) {
      setShake(true);
      setTimeout(() => setShake(false), 600);
      return;
    }
    onVerify && onVerify(code);
  };

  const handleResend = () => {
    setOtp(["", "", "", "", "", ""]);
    setTimer(120);
    setTimerActive(true);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
    // Call resend API here if needed
  };

  const filledCount = otp.filter(Boolean).length;

  if (!show) return null;

  return (
    <div>
      {/* ── Bootstrap-style modal backdrop ── */}
      <div
        className="modal-backdrop fade show"
        onClick={() => onClose && onClose()}
      />

      {/* ── Modal ── */}
      <div
        className="modal fade show"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="loginTfaModalLabel"
        aria-modal="true"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.15)" }}>

            {/* ── Modal Header ── */}
            <div className="modal-header">
              <div className="d-flex align-items-center gap-2">
                <i className="mdi mdi-shield-lock text-primary" style={{ fontSize: "20px" }} />
                <h5 className="modal-title mb-0" id="loginTfaModalLabel">
                  Two-Factor Verification
                </h5>
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => onClose && onClose()}
              />
            </div>

            <div className="modal-body p-4">

              <div className="alert alert-info d-flex align-items-start gap-2 mb-4 py-2 px-3" role="alert" style={{ borderRadius: "8px", fontSize: "13.5px" }}>
                <i className="mdi mdi-information-outline mt-1 flex-shrink-0" style={{ fontSize: "16px" }} />
                <span>
                  Enter the <strong>6-digit verification code</strong> from your authenticator app.
                </span>
              </div>

              <div className="text-center mb-3">
                <span
                  className="avatar-title rounded-circle bg-primary-subtle d-inline-flex align-items-center justify-content-center"
                  style={{ width: "80px", height: "80px" }}
                >
                  <i className="mdi mdi-lock-check text-primary" style={{ fontSize: "45px" }} />
                </span>
              </div>

              <p className="text-center text-muted mb-4" style={{ fontSize: "13.5px" }}>
                Open your authenticator app and enter the code below
              </p>

              {/* OTP Boxes */}
              <form onSubmit={handleSubmit}>
                <div
                  className={`d-flex justify-content-center gap-2 mb-3${shake ? " tfa-shake" : ""}`}
                  onPaste={handlePaste}
                >
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      id={`login-otp-${idx}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={digit}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => handleInput(e, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      autoComplete="one-time-code"
                      className={`form-control text-center fw-bold p-0 otp-input-box${digit ? " border-primary" : ""}${shake ? " border-danger" : ""}`}
                      style={{
                        boxShadow: digit ? "0 0 0 0.15rem rgba(var(--bs-primary-rgb), 0.2)" : "none",
                      }}
                    />
                  ))}
                </div>

                <div className="d-grid mb-2">
                  <button
                    type="submit"
                    className="btn btn-primary waves-effect waves-light"
                    disabled={loading || filledCount < 6}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-check-circle-outline me-2" />
                        <span>Verify & Sign In</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* ── Modal Footer ── */}
            <div className="modal-footer justify-content-center" style={{ borderTop: "1px solid #f0f0f0" }}>
              <small className="text-muted d-flex align-items-center gap-1">
                <i className="mdi mdi-lock-outline" style={{ fontSize: "13px" }} />
                Protected with 256-bit encryption
              </small>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        .tfa-shake {
          animation: tfa-shake 0.5s ease;
        }
        @keyframes tfa-shake {
          0%,100% { transform: translateX(0); }
          20%     { transform: translateX(-6px); }
          40%     { transform: translateX(6px); }
          60%     { transform: translateX(-4px); }
          80%     { transform: translateX(4px); }
        }
        .otp-input-box {
          width: 50px;
          height: 55px;
          font-size: 20px;
          border-radius: 8px;
          border-width: 1px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        @media (max-width: 420px) {
          .otp-input-box {
            width: 40px !important;
            height: 48px !important;
            font-size: 18px !important;
          }
        }
        @media (max-width: 340px) {
          .otp-input-box {
            width: 35px !important;
            height: 42px !important;
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
}
